import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthenticationService } from 'app/auth/service';
import { User } from 'app/auth/models';
import { CoreMenu } from '@core/types';
import { LOCAL_STORAGE_KEYS } from 'app/main/shared/enums/LocalStorage.enum';

@Injectable({
  providedIn: 'root'
})
export class CoreMenuService {
  currentUser: User;
  onItemCollapsed: Subject<any>;
  onItemCollapseToggled: Subject<any>;

  // Private
  private _onMenuRegistered: BehaviorSubject<any>;
  private _onMenuUnregistered: BehaviorSubject<any>;
  private _onMenuChanged: BehaviorSubject<any>;
  private _currentMenuKey: string;
  private _registry: { [key: string]: any } = {};
  private _unsubscribeAll: Subject<any>;
  private _currentEnabledSenderManagement: boolean = false;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   */
  constructor(private _router: Router, private _authenticationService: AuthenticationService) {
    // Set defaults
    this.onItemCollapsed = new Subject();
    this.onItemCollapseToggled = new Subject();
    this._unsubscribeAll = new Subject();

    // Set private defaults
    this._currentMenuKey = null;
    this._onMenuRegistered = new BehaviorSubject(null);
    this._onMenuUnregistered = new BehaviorSubject(null);
    this._onMenuChanged = new BehaviorSubject(null);
  }

  // Add method to filter menu based on company config
  public filterMenuByCompanyConfig(menuItems: CoreMenu[]): CoreMenu[] {
    // Read enableSenderManagement directly from localStorage
    let enableSenderManagement = false;
    try {
      const authData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH));
      enableSenderManagement = authData?.enableSenderManagement ?? false;
    } catch {
      enableSenderManagement = false;
    }
    
    // Add debugging    
    const filteredMenu = this.recursiveMenuFilter(menuItems, enableSenderManagement);
    
    console.log('Filtered menu items:', filteredMenu);
    
    return filteredMenu;
  }

  private recursiveMenuFilter(menuItems: CoreMenu[], enableSenderManagement: boolean): CoreMenu[] {
    return menuItems.filter(item => {
      // Hide owner-senders if sender management is not enabled
      if (item.id === 'owner-senders' && !enableSenderManagement) {
        console.log(`Hiding owner-senders because enableSenderManagement is ${enableSenderManagement}`);
        return false;
      }

      // Filter children recursively
      if (item.children && item.children.length > 0) {
        item.children = this.recursiveMenuFilter(item.children, enableSenderManagement);
        
        // Keep parent if it has remaining children or if it's not a collapsible type
        return item.type !== 'collapsible' || item.children.length > 0;
      }

      return true;
    }).map(item => ({ ...item })); // Clone to avoid mutating original
  }

  // Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * onMenuRegistered
   *
   * @returns {Observable<any>}
   */
  get onMenuRegistered(): Observable<any> {
    return this._onMenuRegistered.asObservable();
  }

  /**
   * onMenuUnregistered
   *
   * @returns {Observable<any>}
   */
  get onMenuUnregistered(): Observable<any> {
    return this._onMenuUnregistered.asObservable();
  }

  /**
   * onMenuChanged
   *
   * @returns {Observable<any>}
   */
  get onMenuChanged(): Observable<any> {
    return this._onMenuChanged.asObservable();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Register the provided menu with the provided key
   *
   * @param key
   * @param menu
   */
  register(key, menu): void {
    // Confirm if the key already used
    if (this._registry[key]) {
      console.error(`Menu with the key '${key}' already exists. Either unregister it first or use a unique key.`);
      return;
    }

    // Add debugging to see what menu is being registered
    console.log(`Registering menu with key: ${key}`, menu);

    // Add to registry
    this._registry[key] = menu;

    // Notify subject
    this._onMenuRegistered.next([key, menu]);
  }

  /**
   * Unregister the menu from the registry
   *
   * @param key
   */
  unregister(key): void {
    // Confirm if the menu exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);
    }

    // Unregister sidebar
    delete this._registry[key];

    // Notify subject
    this._onMenuUnregistered.next(key);
  }

  /**
   * Get menu from registry by key
   *
   * @param key
   * @returns {any}
   */
  getMenu(key): any {
    // Confirm if the menu exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);
      return;
    }

    // Return sidebar
    return this._registry[key];
  }

  /**
   * Get current menu
   *
   * @returns {any}
   */
  getCurrentMenu(): any {
    if (!this._currentMenuKey) {
      console.warn(`The current menu is not set.`);
      return;
    }

    return this.getMenu(this._currentMenuKey);
  }

  /**
   * Set menu with the key as the current menu
   *
   * @param key
   */
  setCurrentMenu(key): void {
    // Confirm if the sidebar exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);
      return;
    }

    // Set current menu key
    this._currentMenuKey = key;

    // Apply filtering when setting current menu
    const originalMenu = this._registry[key];
    const filteredMenu = this.filterMenuByCompanyConfig(originalMenu);

    // Notify subject with filtered menu
    this._onMenuChanged.next(filteredMenu);
  }

  changeMenu = (menu): void => {
    // Apply filtering when changing menu
    const filteredMenu = this.filterMenuByCompanyConfig(menu);
    this._onMenuRegistered.next(['menu', filteredMenu]);
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
